/**
 * Created by Ella Ma on 12/27/19.
 * Description:
 *
 * ------ maintenance history ------
 */

import { Component, OnInit, OnDestroy, Input } from '@angular/core';
import { PrintPreviewDashboardService } from './print-preview-dashboard.service';
import { Subscription, Subject } from 'rxjs';
import { GridsterConfig, GridType, CompactType } from 'angular-gridster2';
import { StoreQuerierService } from '../../../services/store-querier.service';
import { getEntireTileSelector } from '../../../redux/reducers/dashboard.reducer';
import * as _ from 'lodash';
import { PopUpData, TamDashboardPopUpService } from '../../../components/tam-dashboard/tam-dashboard-popup.service';
import { TamDashboardTileType, TamDashboardEvents } from '../../../components/tam-dashboard/tam-dashboard.model';

const SAVA_PDF_NAME = 'Tamale RMS';

@Component({
    selector: 'tam-print-preview-dashboard',
    templateUrl: './print-preview-dashboard.component.html',
    styleUrls: ['./print-preview-dashboard.component.scss']
})

export class PrintPreviewDashboardComponent implements OnInit, OnDestroy {

    @Input() fromPage?: string;

    dialogOpened = false;
    gsOptions: GridsterConfig;
    gsItems: any = [];
    popUpParams: PopUpData = new PopUpData();
    feedbackSubject$ = new Subject();
    isGridTileIncluded = false;
    loading = true;
    screenType: string;
    renderDashboardReadyForServer = false;

    private _renderCount = {
        renderReadyTile: [],
        loadedDataTile: []
    };
    private _renderSubject$ = new Subject();
    private _destroySubscriptions: Array<Subscription> = [];

    constructor(
        private _printPreviewDashboardService: PrintPreviewDashboardService,
        private _storeQuerier: StoreQuerierService,
        private _dashboardPopUpService: TamDashboardPopUpService
    ) { }

    ngOnInit() {
        this.gsOptions = {
            setGridSize: true,
            fixedRowHeight: 150,
            minCols: 6,
            maxCols: 6,
            margin: 0,
            gridType: GridType.VerticalFixed,
            compactType: CompactType.CompactUp
        };
        this._destroySubscriptions.push(
            this._printPreviewDashboardService.openDialog$.subscribe((data) => {
                this.dialogOpened = !!data.isOpen;
                this.screenType = data.screenType;
                if (this.dialogOpened) {
                    // save .pdf name
                    document.title = data.name || SAVA_PDF_NAME;
                    this.loading = true;
                    this._renderCount.renderReadyTile = [];
                    this._renderCount.loadedDataTile = [];
                    this._renderSubject$.next();
                }
            }),
            this._renderSubject$.subscribe((data) => {
                this._initGsItems();
            }),
            this._dashboardPopUpService.toUpdatePage$.subscribe((data) => {
                this.popUpParams = data;
            }),
            this.feedbackSubject$.subscribe((payload) => {
                this._feedbackSubjectEvents(payload);
            })
        );
    }

    ngOnDestroy() {
        this._destroySubscriptions.forEach(item => item.unsubscribe());
    }

    closeDialog() {
        document.title = SAVA_PDF_NAME;
        this.dialogOpened = false;
    }

    getStyle(item) {
        if (item.type === TamDashboardTileType.GRID) {
            return {
                'width': item.cols / 6 * 100 + '%',
            };
        } else if (item.type === TamDashboardTileType.METADATA ||
            item.type === TamDashboardTileType.BAR_CHART ||
            item.type === TamDashboardTileType.NOTES ||
            item.type === TamDashboardTileType.EDITOR) {
            return {
                'width': item.cols / 6 * 100 + '%',
                'min-height': item.rows * 150 + 'px',
            };
        } else {
            return {
                'width': item.cols / 6 * 100 + '%',
                'height': item.rows * 150 + 'px',
            };
        }
    }

    printPage() {
        window.print();
    }

    triggerAllXLSExport(e: any) {
        const printPreviewElements = document.getElementsByTagName('tam-print-preview-dashboard');
        if (printPreviewElements && printPreviewElements.length > 0) {
            const hiddenBtns = printPreviewElements[0].querySelectorAll('.excelexportBtn');
            if (hiddenBtns && hiddenBtns.length > 0) {
                for (let i = 0; i < hiddenBtns.length; i++) {
                    hiddenBtns[i]['click']();
                }
            }
        }
    }

    /**
     * (1) Pie + grid
     * No matter what the grid config is,
     * If the grid is displayed, it will be displayed on the page;
     * if the remaining space is not displayed, it will be displayed on the next page
     * (2) Grid + pie
     * if ag grid check break-page, the pie chart must displayed on the next page
     * if ag grid uncheck break-page
     *    the remaining space is displayed pie, it will be displayed on the page;
     *    the remaining space is not displayed, it will be displayed on the next page
     * (3) GridA + GridB
     * if GridA check break-page, GridB must displayed on the next page
     * if GridB uncheck break-page, GridB must displayed on the page no matter the remaining space is enough
     */
    breakPageClass(item, i) {
        if (item.type === TamDashboardTileType.GRID) {
            if (this.gsItems[i - 1]) {
                // not support misplaced layout
                const preTiles = this.gsItems.filter(tile => item.y === (tile.rows + tile.y) && tile.type === TamDashboardTileType.GRID);
                if (preTiles && preTiles.length > 0) {
                    if (preTiles.every(tile => {
                        return tile.config.visualization.settings.gridSetting && !tile.config.visualization.settings.gridSetting.pageBreak;
                    })) {
                        return null;
                    }
                }
            }
        }
        return {
            'page-break-inside': true
        };
    }

    private _initGsItems() {
        const print = this._storeQuerier.queryBySelector(getEntireTileSelector);
        // only screen out the note tiles without any data.
        const gsItems = print.tile.filter(tile => !(tile.info.type === 6 &&
            (!tile.data || !tile.data.threadConfigs || tile.data.threadConfigs.size === 0))).map(tile => ({
                id: tile.id,
                data: tile.data,
                config: tile.config,
                ...tile.info,
            }));
        const groups = this._setGsItems(gsItems);
        this.gsItems = this._addBlankTile(groups);
        if (this.gsItems.length === 0) {
            this.loading = false;
        }
    }

    private _setGsItems(gsItems) {
        const groups = gsItems.sort((a, b) => {
            if (a.y === b.y) {
                return a.x - b.x;
            }
            return a.y - b.y;
        });
        return groups;
    }

    private _addBlankTile(groups) {
        const tempArrs = _.groupBy(groups, (item) => item.y);
        for (const item in tempArrs) {
            if (tempArrs.hasOwnProperty(item)) {
                let x = 0, j = 0;
                const tempItem = tempArrs[item];
                const arr = [];
                const minY = _.minBy(tempArrs[item], data => data.rows);
                while (arr.length === 0 || (arr[arr.length - 1].x + arr[arr.length - 1].cols < 6)) {
                    let count;
                    if (!tempItem[j] || tempItem[j].x !== x) {
                        count = 1;
                        arr.push({
                            cols: 1,
                            rows: minY.rows,
                            x: arr[arr.length - 1] ? arr[arr.length - 1].x + arr[arr.length - 1].cols : 0,
                            y: minY.y,
                            type: null
                        });
                    } else {
                        count = tempItem[j].cols;
                        arr.push(tempItem[j]);
                        j++;
                    }
                    x += count;
                }
                // @ts-ignore
                tempArrs[item] = arr;
            }
        }
        const tempGroup = [];
        Object.values(tempArrs).forEach((item) => {
            tempGroup.push(...item);
        });
        return tempGroup;
    }

    private _feedbackSubjectEvents(payload) {
        if (payload.type === TamDashboardEvents.TILE_LOADED_DATA) {
            const index = this._renderCount.loadedDataTile.findIndex(item => item === payload.id);
            if (index === -1) {
                this._renderCount.loadedDataTile.push(payload.id);
                this._setLoadingStatus();
            }
        } else if (payload.type === TamDashboardEvents.TILE_AFTER_VIEW_INIT) {
            const index = this._renderCount.renderReadyTile.findIndex(item => item === payload.id);
            if (index === -1) {
                this._renderCount.renderReadyTile.push(payload.id);
                this._setLoadingStatus();
            }
        }
    }

    private _setLoadingStatus() {
        const len = this.gsItems.filter(item => (item.data || item.data === 0)).length;
        const tempLen = this.gsItems.filter(item => item.id).length;
        const dataLen = this._renderCount.loadedDataTile.length;
        const readyLen = this._renderCount.renderReadyTile.length;
        if ((dataLen === len && readyLen === len) ||
            (dataLen > 0 && readyLen > 0 && dataLen === readyLen && readyLen === tempLen && tempLen > len)) {
            const hasGridTile = this.gsItems.some(item => item.type === TamDashboardTileType.GRID);
            this.isGridTileIncluded = hasGridTile;
            // grid render slowly
            const time = hasGridTile ? 3000 : 1000;
            setTimeout(() => {
                this.loading = false;
                if (this.fromPage === 'server') {
                    // Server generates PDF identification node
                    setTimeout(() => {
                        this.renderDashboardReadyForServer = true;
                    }, time);
                }
            }, time);
        }
    }
}
